import { apiFetch, forgetCsrfToken } from './client';

/** What somebody may do on this instance. Given by an administrator (Phase 24). */
export type Role = 'MEMBER' | 'MODERATOR' | 'ADMIN';

export interface Account {
  id: string;
  email: string;
  handle: string;
  display_name: string;
  bio: string;
  is_email_verified: boolean;
  is_staff: boolean;
  /**
   * Read-only, and never what decides: the backend asks again on every
   * administration request. It is here so the app can avoid offering a screen
   * that would only answer 403 (Rule 7).
   */
  role: Role;
  date_joined: string;
}

export interface DetailResponse {
  detail: string;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export interface RegisterInput {
  email: string;
  password: string;
  handle: string;
  display_name: string;
}

export const register = (input: RegisterInput): Promise<DetailResponse> =>
  post<DetailResponse>('/auth/register', input);

// Django rotates the CSRF token on sign-in and sign-out, so the one we are
// holding stops being the one the server will accept. Forgetting it here means
// the next mutation asks for a fresh one instead of being refused.
export async function login(email: string, password: string): Promise<Account> {
  const account = await post<Account>('/auth/login', { email, password });
  forgetCsrfToken();
  return account;
}

export async function logout(): Promise<void> {
  await post<void>('/auth/logout', {});
  forgetCsrfToken();
}

export const fetchMe = (): Promise<Account> => apiFetch<Account>('/me');

export const updateMe = (changes: Partial<Pick<Account, 'handle' | 'display_name' | 'bio'>>) =>
  apiFetch<Account>('/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });

export const verifyEmail = (token: string): Promise<DetailResponse> =>
  post<DetailResponse>('/auth/email/verify', { token });

export const resendVerification = (email: string): Promise<DetailResponse> =>
  post<DetailResponse>('/auth/email/verify/resend', { email });

export const forgotPassword = (email: string): Promise<DetailResponse> =>
  post<DetailResponse>('/auth/password/forgot', { email });

export const resetPassword = (token: string, password: string): Promise<DetailResponse> =>
  post<DetailResponse>('/auth/password/reset', { token, password });

export const changePassword = (
  currentPassword: string,
  newPassword: string,
): Promise<DetailResponse> =>
  post<DetailResponse>('/auth/password/change', {
    current_password: currentPassword,
    new_password: newPassword,
  });
